import AssetAdapter from '../adapters/asset.js';
export class ExchangeAssetAvailability {
    #assetsModule;
    #exchangeProviders;
    #assetAvailabilityService;
    #enabledAssetsAtom;
    #exchangeableAssetsAtom;
    #prefetchAssetsOnLoad;
    #loaded = false;
    #subscriptions = [];
    constructor(params) {
        this.#assetsModule = params.assetsModule;
        this.#exchangeProviders = params.exchangeProviders;
        this.#assetAvailabilityService = params.exchangeProviders.getAssetAvailabilityService();
        this.#enabledAssetsAtom = params.enabledAssetsAtom;
        this.#exchangeableAssetsAtom = params.exchangeableAssetsAtom;
        this.#prefetchAssetsOnLoad = params.config.prefetchAssetsOnLoad ?? true;
    }
    getSupportedFromAssetIds = async () => {
        const assetAdapter = new AssetAdapter({ assetsModule: this.#assetsModule });
        const exchangeFromAssetIds = await this.#assetAvailabilityService.getSupportedFromAssetIds();
        return [...exchangeFromAssetIds].map((exchangeId) => assetAdapter.toWalletId(exchangeId));
    };
    getSupportedToAssetIds = async (from) => {
        const assetAdapter = new AssetAdapter({ assetsModule: this.#assetsModule });
        const exchangeFromId = assetAdapter.toExchangeId(from);
        const exchangeToAssetIds = await this.#assetAvailabilityService.getSupportedToAssetIds(exchangeFromId);
        return [...exchangeToAssetIds].map((exchangeId) => assetAdapter.toWalletId(exchangeId));
    };
    isAssetExchangeable = async (assetId) => {
        const assetAdapter = new AssetAdapter({ assetsModule: this.#assetsModule });
        const exchangeId = assetAdapter.toExchangeId(assetId);
        return this.#assetAvailabilityService.isAssetExchangeable(exchangeId);
    };
    isExactOutSupported = async ({ fromTicker, toTicker, }) => {
        const assetAdapter = new AssetAdapter({ assetsModule: this.#assetsModule });
        const fromExchangeId = assetAdapter.toExchangeId(fromTicker);
        const toExchangeId = assetAdapter.toExchangeId(toTicker);
        const pairId = `${fromExchangeId}_${toExchangeId}`;
        return this.#assetAvailabilityService.isExactOutSupported(pairId);
    };
    startAssetAvailabilityMonitor = async () => {
        if (this.#loaded)
            return;
        const enabledAssetsAtomValue = await this.#enabledAssetsAtom.get();
        const enabledAssetIds = this.#getAssetIds(Object.keys(enabledAssetsAtomValue).filter((assetName) => enabledAssetsAtomValue[assetName]));
        const enabledSources = this.#exchangeProviders.getSources();
        await this.#assetAvailabilityService.start(enabledAssetIds, enabledSources);
        const assetAdapter = new AssetAdapter({ assetsModule: this.#assetsModule });
        const exchangeFromAssetIds = await this.#assetAvailabilityService.getSupportedFromAssetIds();
        const walletFromAssetIds = [...exchangeFromAssetIds].map((exchangeId) => assetAdapter.toWalletId(exchangeId));
        await this.#exchangeableAssetsAtom.set((s) => ({
            ...s,
            fromAssetIds: walletFromAssetIds,
        }));
        this.#loaded = true;
    };
    update = async (assetIds, sources) => {
        await this.#assetAvailabilityService.update(assetIds, sources);
    };
    load = () => {
        const enabledAssetsAtomSubscription = this.#enabledAssetsAtom.observe(this.#handleEnabledAssetsChange);
        this.#subscriptions.push(enabledAssetsAtomSubscription);
    };
    start = async () => {
        if (this.#prefetchAssetsOnLoad) {
            await this.startAssetAvailabilityMonitor();
        }
    };
    stop = () => {
        this.#subscriptions.forEach((unsubscribe) => unsubscribe());
        this.#assetAvailabilityService.stop();
    };
    #handleEnabledAssetsChange = async (value) => {
        if (!this.#loaded)
            return;
        const enabledAssetIds = this.#getAssetIds(Object.keys(value).filter((assetName) => value[assetName]));
        const enabledSources = this.#exchangeProviders.getSources();
        void this.#assetAvailabilityService.update(enabledAssetIds, enabledSources);
    };
    #getAssetIds = (assetNames) => {
        const assetAdapter = new AssetAdapter({ assetsModule: this.#assetsModule });
        return assetNames
            .map((assetName) => {
            const asset = this.#assetsModule.getAsset(assetName);
            if (!asset)
                return null;
            return assetAdapter.toExchangeId(asset.ticker);
        })
            .filter(Boolean);
    };
}
const exchangeAssetAvailabilityFactory = (args) => new ExchangeAssetAvailability({ ...args });
const exchangeAssetAvailabilityDefinition = {
    id: 'exchangeAssetAvailability',
    type: 'module',
    factory: exchangeAssetAvailabilityFactory,
    dependencies: [
        'config',
        'assetsModule',
        'enabledAssetsAtom',
        'exchangeableAssetsAtom',
        'exchangeProviders',
    ],
};
export default exchangeAssetAvailabilityDefinition;
